import type { Lang } from '../types';

export interface ManagerProfileText {
  pageTitle: string;
  tabProfile: string;
  tabTtlock: string;
  tabCleaners: string;
  tabContacts: string;
  tabPaymentMethods: string;
  tabSubscription: string;
  tabPrivacy: string;
  privacyTitle: string;
  privacyDesc: string;
  privacyManageLink: string;
  profileDescription: string;
  loadingProfile: string;
  sectionPersonalInfo: string;
  fieldEmailLogin: string;
  fieldFirstName: string;
  fieldLastName: string;
  fieldTelephone: string;
  fieldIco: string;
  saveProfileBtn: string;
  savingProfileBtn: string;
  sectionChangePassword: string;
  fieldCurrentPassword: string;
  fieldNewPassword: string;
  fieldConfirmPassword: string;
  changePasswordBtn: string;
  changingPasswordBtn: string;
  profileSaved: string;
  passwordChanged: string;
  errorPasswordTooShort: string;
  errorPasswordMismatch: string;
  ttlockDescription: string;
  ttlockNoAccount: string;
  ttlockConnectedAs: string;
  ttlockTokenExpired: string;
  ttlockTokenValid: (date: string, days: number) => string;
  ttlockReconnectTitle: string;
  ttlockConnectTitle: string;
  ttlockUsernameLabel: string;
  ttlockPasswordLabel: string;
  connectingBtn: string;
  connectBtn: string;
  locksTitle: (count: number) => string;
  noLocksFound: string;
  lockActive: string;
  lockInactive: string;
  configureBtn: string;
  ttlockConnected: string;
  cleanersDescription: string;
  addCleanerBtn: string;
  editCleanerTitle: string;
  newCleanerTitle: string;
  cleanerNameLabel: string;
  cleanerScheduleDaysLabel: string;
  cleanerScheduleDaysHint: string;
  cleanerApartmentsLabel: string;
  noApartmentsFound: string;
  updateBtn: string;
  createBtn: string;
  loadingCleaners: string;
  noCleaners: string;
  daysVisible: string;
  noApartmentsAssigned: string;
  copyLinkBtn: string;
  copiedBtn: string;
  cleanerNameRequired: string;
  cleanerUpdated: string;
  cleanerCreated: string;
  cleanerDeleted: string;
  confirmDeleteCleaner: string;
  contactsDescription: string;
  addContactBtn: string;
  editContactTitle: string;
  newContactTitle: string;
  contactNameLabel: string;
  contactPhoneLabel: string;
  contactEmailLabel: string;
  contactCompanyLabel: string;
  contactIcoLabel: string;
  contactAddressLabel: string;
  savingBtn: string;
  saveBtn: string;
  loadingContacts: string;
  noContacts: string;
  contactsEmptyBody: string;
  noContactDetails: string;
  contactUpdated: string;
  contactCreated: string;
  confirmDeleteContact: (name: string) => string;
}

export const managerProfileText: Record<Lang, ManagerProfileText> = {
  cs: {
    pageTitle: 'Nastavení',
    tabProfile: 'Můj profil',
    tabTtlock: '🔐 Smart Lock',
    tabCleaners: '🧹 Uklízeči',
    tabContacts: '📋 Kontakty',
    tabPaymentMethods: '💳 Platební metody',
    tabSubscription: '🧾 Předplatné',
    tabPrivacy: '🔒 Soukromí',
    privacyTitle: 'Reklamní měření',
    privacyDesc: 'Při registraci jste mohli dobrovolně souhlasit s měřením reklamy Google Ads. Souhlas můžete kdykoli odvolat.',
    privacyManageLink: 'Spravovat souhlas s reklamním měřením',
    profileDescription: 'Aktualizujte své osobní údaje a heslo. E-mail je vaše přihlašovací jméno a nelze jej změnit.',
    loadingProfile: 'Načítám profil…',
    sectionPersonalInfo: 'Osobní informace',
    fieldEmailLogin: 'E-mail (přihlášení)',
    fieldFirstName: 'Jméno',
    fieldLastName: 'Příjmení',
    fieldTelephone: 'Telefon',
    fieldIco: 'IČO',
    saveProfileBtn: 'Uložit profil',
    savingProfileBtn: 'Ukládám…',
    sectionChangePassword: 'Změna hesla',
    fieldCurrentPassword: 'Aktuální heslo',
    fieldNewPassword: 'Nové heslo',
    fieldConfirmPassword: 'Potvrdit nové heslo',
    changePasswordBtn: 'Změnit heslo',
    changingPasswordBtn: 'Měním heslo…',
    profileSaved: 'Osobní informace uloženy',
    passwordChanged: 'Heslo bylo úspěšně změněno',
    errorPasswordTooShort: 'Nové heslo musí mít alespoň 8 znaků',
    errorPasswordMismatch: 'Nové heslo a potvrzení se neshodují',
    ttlockDescription: 'Propojte svůj TTLock účet pro automatické generování PIN kódů k rezervacím.',
    ttlockNoAccount: 'Žádný TTLock účet není připojen.',
    ttlockConnectedAs: 'Připojeno jako',
    ttlockTokenExpired: 'Token vypršel — znovu se připojte níže',
    ttlockTokenValid: (date, days) => `Token platný · vyprší ${date} (zbývá ${days} dní)`,
    ttlockReconnectTitle: 'Znovu připojit účet',
    ttlockConnectTitle: 'Připojit účet',
    ttlockUsernameLabel: 'TTLock uživatelské jméno',
    ttlockPasswordLabel: 'TTLock heslo',
    connectingBtn: 'Připojuji…',
    connectBtn: 'Připojit',
    locksTitle: (count) => `Zámky (${count})`,
    noLocksFound: 'Žádné zámky nenalezeny.',
    lockActive: 'Aktivní',
    lockInactive: 'Neaktivní',
    configureBtn: 'Konfigurovat',
    ttlockConnected: 'TTLock účet byl úspěšně připojen.',
    cleanersDescription: 'Spravujte úklidový personál a jejich soukromé odkazy na plánování.',
    addCleanerBtn: '+ Přidat uklízeče',
    editCleanerTitle: 'Upravit uklízeče',
    newCleanerTitle: 'Nový uklízeč',
    cleanerNameLabel: 'Jméno *',
    cleanerScheduleDaysLabel: 'Viditelnost rozvrhu (dny)',
    cleanerScheduleDaysHint: 'Kolik dní dopředu může uklízeč vidět.',
    cleanerApartmentsLabel: 'Přiřazené apartmány',
    noApartmentsFound: 'Žádné apartmány nenalezeny.',
    updateBtn: 'Aktualizovat',
    createBtn: 'Vytvořit',
    loadingCleaners: 'Načítám…',
    noCleaners: 'Zatím žádní uklízeči. Klikněte na „+ Přidat uklízeče" pro začátek.',
    daysVisible: 'dní viditelných',
    noApartmentsAssigned: 'Žádné přiřazené apartmány',
    copyLinkBtn: 'Kopírovat odkaz',
    copiedBtn: '✓ Zkopírováno',
    cleanerNameRequired: 'Jméno je povinné',
    cleanerUpdated: 'Uklízeč aktualizován.',
    cleanerCreated: 'Uklízeč vytvořen.',
    cleanerDeleted: 'Uklízeč smazán.',
    confirmDeleteCleaner: 'Smazat tohoto uklízeče?',
    contactsDescription: 'Opakovaně použitelné kontakty, které můžete přiřadit k apartmánům místo opakovaného vyplňování kontaktních údajů.',
    addContactBtn: '+ Přidat kontakt',
    editContactTitle: 'Upravit kontakt',
    newContactTitle: 'Nový kontakt',
    contactNameLabel: 'Jméno *',
    contactPhoneLabel: 'Telefon',
    contactEmailLabel: 'E-mail',
    contactCompanyLabel: 'Název právnické osoby',
    contactIcoLabel: 'IČO/DIČ',
    contactAddressLabel: 'Místo pobytu nebo sídlo právnické osoby',
    savingBtn: 'Ukládám…',
    saveBtn: 'Uložit',
    loadingContacts: 'Načítám kontakty…',
    noContacts: 'Zatím žádné kontakty.',
    contactsEmptyBody: 'Kontakty slouží jako opakovaně použitelné osoby (majitel, správce, uklízeč), které lze přiřadit k apartmánu. Host vidí jméno a telefon kontaktu v registračním formuláři.',
    noContactDetails: 'Žádné kontaktní údaje',
    contactUpdated: 'Kontakt aktualizován.',
    contactCreated: 'Kontakt vytvořen.',
    confirmDeleteContact: (name) => `Smazat kontakt „${name}"?`,
  },
  en: {
    pageTitle: 'Settings',
    tabProfile: 'My Profile',
    tabTtlock: '🔐 Smart Lock',
    tabCleaners: '🧹 Cleaners',
    tabContacts: '📋 Contacts',
    tabPaymentMethods: '💳 Payment Methods',
    tabSubscription: '🧾 Subscription',
    tabPrivacy: '🔒 Privacy',
    privacyTitle: 'Advertising measurement',
    privacyDesc: 'When you signed up, you could optionally agree to Google Ads measurement. You can withdraw that consent at any time.',
    privacyManageLink: 'Manage advertising measurement consent',
    profileDescription: 'Update your personal data and password. Email is your login and cannot be changed.',
    loadingProfile: 'Loading profile...',
    sectionPersonalInfo: 'Personal Information',
    fieldEmailLogin: 'Email (login)',
    fieldFirstName: 'First name',
    fieldLastName: 'Last name',
    fieldTelephone: 'Telephone',
    fieldIco: 'Company ID (IČO)',
    saveProfileBtn: 'Save Profile',
    savingProfileBtn: 'Saving...',
    sectionChangePassword: 'Change Password',
    fieldCurrentPassword: 'Current password',
    fieldNewPassword: 'New password',
    fieldConfirmPassword: 'Confirm new password',
    changePasswordBtn: 'Change Password',
    changingPasswordBtn: 'Updating...',
    profileSaved: 'Personal information saved',
    passwordChanged: 'Password changed successfully',
    errorPasswordTooShort: 'New password must have at least 8 characters',
    errorPasswordMismatch: 'New password and confirmation do not match',
    ttlockDescription: 'Connect your TTLock account to enable automatic PIN generation for reservations.',
    ttlockNoAccount: 'No TTLock account connected.',
    ttlockConnectedAs: 'Connected as',
    ttlockTokenExpired: 'Token expired — reconnect below',
    ttlockTokenValid: (date, days) => `Token valid · expires ${date} (${days}d left)`,
    ttlockReconnectTitle: 'Reconnect Account',
    ttlockConnectTitle: 'Connect Account',
    ttlockUsernameLabel: 'TTLock Username',
    ttlockPasswordLabel: 'TTLock Password',
    connectingBtn: 'Connecting...',
    connectBtn: 'Connect',
    locksTitle: (count) => `Locks (${count})`,
    noLocksFound: 'No locks found.',
    lockActive: 'Active',
    lockInactive: 'Inactive',
    configureBtn: 'Configure',
    ttlockConnected: 'TTLock account connected successfully.',
    cleanersDescription: 'Manage cleaning staff and their private schedule links.',
    addCleanerBtn: '+ Add Cleaner',
    editCleanerTitle: 'Edit Cleaner',
    newCleanerTitle: 'New Cleaner',
    cleanerNameLabel: 'Name *',
    cleanerScheduleDaysLabel: 'Schedule visibility (days)',
    cleanerScheduleDaysHint: 'How many days ahead the cleaner can see.',
    cleanerApartmentsLabel: 'Assigned Apartments',
    noApartmentsFound: 'No apartments found.',
    updateBtn: 'Update',
    createBtn: 'Create',
    loadingCleaners: 'Loading…',
    noCleaners: 'No cleaners yet. Click "+ Add Cleaner" to get started.',
    daysVisible: 'days visible',
    noApartmentsAssigned: 'No apartments assigned',
    copyLinkBtn: 'Copy Link',
    copiedBtn: '✓ Copied',
    cleanerNameRequired: 'Name is required',
    cleanerUpdated: 'Cleaner updated.',
    cleanerCreated: 'Cleaner created.',
    cleanerDeleted: 'Cleaner deleted.',
    confirmDeleteCleaner: 'Delete this cleaner?',
    contactsDescription: 'Reusable contacts you can assign to apartments instead of filling in contact details each time.',
    addContactBtn: '+ Add Contact',
    editContactTitle: 'Edit Contact',
    newContactTitle: 'New Contact',
    contactNameLabel: 'Name *',
    contactPhoneLabel: 'Phone',
    contactEmailLabel: 'Email',
    contactCompanyLabel: 'Company / legal entity name',
    contactIcoLabel: 'ICO/DIC (business ID/tax ID)',
    contactAddressLabel: 'Place of residence or registered office',
    savingBtn: 'Saving…',
    saveBtn: 'Save',
    loadingContacts: 'Loading contacts...',
    noContacts: 'No contacts yet.',
    contactsEmptyBody: 'Contacts are reusable people (owner, manager, cleaner) you can assign to an apartment. Guests will see the contact\'s name and phone on the check-in form.',
    noContactDetails: 'No contact details',
    contactUpdated: 'Contact updated.',
    contactCreated: 'Contact created.',
    confirmDeleteContact: (name) => `Delete contact "${name}"?`,
  },
  vi: {
    pageTitle: 'Cài đặt',
    tabProfile: 'Hồ sơ của tôi',
    tabTtlock: '🔐 Khóa thông minh',
    tabCleaners: '🧹 Nhân viên dọn dẹp',
    tabContacts: '📋 Danh bạ',
    tabPaymentMethods: '💳 Phương thức thanh toán',
    tabSubscription: '🧾 Đăng ký dịch vụ',
    tabPrivacy: '🔒 Quyền riêng tư',
    privacyTitle: 'Đo lường quảng cáo',
    privacyDesc: 'Khi đăng ký, bạn có thể tự nguyện đồng ý cho việc đo lường quảng cáo của Google Ads. Bạn có thể rút lại sự đồng ý này bất cứ lúc nào.',
    privacyManageLink: 'Quản lý sự đồng ý đo lường quảng cáo',
    profileDescription: 'Cập nhật thông tin cá nhân và mật khẩu. Email là tên đăng nhập và không thể thay đổi.',
    loadingProfile: 'Đang tải hồ sơ…',
    sectionPersonalInfo: 'Thông tin cá nhân',
    fieldEmailLogin: 'Email (đăng nhập)',
    fieldFirstName: 'Tên',
    fieldLastName: 'Họ',
    fieldTelephone: 'Điện thoại',
    fieldIco: 'Mã số doanh nghiệp (IČO)',
    saveProfileBtn: 'Lưu hồ sơ',
    savingProfileBtn: 'Đang lưu…',
    sectionChangePassword: 'Đổi mật khẩu',
    fieldCurrentPassword: 'Mật khẩu hiện tại',
    fieldNewPassword: 'Mật khẩu mới',
    fieldConfirmPassword: 'Xác nhận mật khẩu mới',
    changePasswordBtn: 'Đổi mật khẩu',
    changingPasswordBtn: 'Đang cập nhật…',
    profileSaved: 'Thông tin cá nhân đã được lưu',
    passwordChanged: 'Mật khẩu đã được thay đổi thành công',
    errorPasswordTooShort: 'Mật khẩu mới phải có ít nhất 8 ký tự',
    errorPasswordMismatch: 'Mật khẩu mới và xác nhận không khớp',
    ttlockDescription: 'Kết nối tài khoản TTLock để tự động tạo mã PIN cho lịch đặt.',
    ttlockNoAccount: 'Chưa kết nối tài khoản TTLock.',
    ttlockConnectedAs: 'Đã kết nối với',
    ttlockTokenExpired: 'Token hết hạn — kết nối lại bên dưới',
    ttlockTokenValid: (date, days) => `Token hợp lệ · hết hạn ${date} (còn ${days} ngày)`,
    ttlockReconnectTitle: 'Kết nối lại tài khoản',
    ttlockConnectTitle: 'Kết nối tài khoản',
    ttlockUsernameLabel: 'Tên đăng nhập TTLock',
    ttlockPasswordLabel: 'Mật khẩu TTLock',
    connectingBtn: 'Đang kết nối…',
    connectBtn: 'Kết nối',
    locksTitle: (count) => `Khóa (${count})`,
    noLocksFound: 'Không tìm thấy khóa.',
    lockActive: 'Hoạt động',
    lockInactive: 'Không hoạt động',
    configureBtn: 'Cấu hình',
    ttlockConnected: 'Tài khoản TTLock đã được kết nối thành công.',
    cleanersDescription: 'Quản lý nhân viên dọn dẹp và liên kết lịch làm việc riêng của họ.',
    addCleanerBtn: '+ Thêm nhân viên',
    editCleanerTitle: 'Sửa nhân viên',
    newCleanerTitle: 'Nhân viên mới',
    cleanerNameLabel: 'Tên *',
    cleanerScheduleDaysLabel: 'Số ngày xem lịch trước',
    cleanerScheduleDaysHint: 'Nhân viên có thể xem trước bao nhiêu ngày.',
    cleanerApartmentsLabel: 'Căn hộ được phân công',
    noApartmentsFound: 'Không tìm thấy căn hộ.',
    updateBtn: 'Cập nhật',
    createBtn: 'Tạo',
    loadingCleaners: 'Đang tải…',
    noCleaners: 'Chưa có nhân viên nào. Nhấn "+ Thêm nhân viên" để bắt đầu.',
    daysVisible: 'ngày hiển thị',
    noApartmentsAssigned: 'Chưa phân công căn hộ',
    copyLinkBtn: 'Sao chép liên kết',
    copiedBtn: '✓ Đã sao chép',
    cleanerNameRequired: 'Tên là bắt buộc',
    cleanerUpdated: 'Đã cập nhật nhân viên.',
    cleanerCreated: 'Đã tạo nhân viên.',
    cleanerDeleted: 'Đã xóa nhân viên.',
    confirmDeleteCleaner: 'Xóa nhân viên này?',
    contactsDescription: 'Danh bạ có thể tái sử dụng để gán cho căn hộ thay vì nhập thủ công mỗi lần.',
    addContactBtn: '+ Thêm liên hệ',
    editContactTitle: 'Sửa liên hệ',
    newContactTitle: 'Liên hệ mới',
    contactNameLabel: 'Tên *',
    contactPhoneLabel: 'Điện thoại',
    contactEmailLabel: 'Email',
    contactCompanyLabel: 'Tên công ty / pháp nhân',
    contactIcoLabel: 'IČO/DIČ (mã số kinh doanh/mã số thuế)',
    contactAddressLabel: 'Nơi cư trú hoặc trụ sở pháp nhân',
    savingBtn: 'Đang lưu…',
    saveBtn: 'Lưu',
    loadingContacts: 'Đang tải danh bạ…',
    noContacts: 'Chưa có liên hệ nào.',
    contactsEmptyBody: 'Liên hệ là những người có thể tái sử dụng (chủ nhà, quản lý, nhân viên dọn dẹp) mà bạn có thể gán cho căn hộ. Khách sẽ thấy tên và số điện thoại liên hệ trong biểu mẫu check-in.',
    noContactDetails: 'Không có thông tin liên hệ',
    contactUpdated: 'Đã cập nhật liên hệ.',
    contactCreated: 'Đã tạo liên hệ.',
    confirmDeleteContact: (name) => `Xóa liên hệ "${name}"?`,
  },
  uk: {
    pageTitle: 'Налаштування',
    tabProfile: 'Мій профіль',
    tabTtlock: '🔐 Розумний замок',
    tabCleaners: '🧹 Прибиральники',
    tabContacts: '📋 Контакти',
    tabPaymentMethods: '💳 Платіжні методи',
    tabSubscription: '🧾 Підписка',
    tabPrivacy: '🔒 Конфіденційність',
    privacyTitle: 'Вимірювання реклами',
    privacyDesc: 'Під час реєстрації ви могли добровільно погодитися на вимірювання реклами Google Ads. Цю згоду можна відкликати в будь-який момент.',
    privacyManageLink: 'Керувати згодою на вимірювання реклами',
    profileDescription: 'Оновіть особисті дані та пароль. Електронна пошта є логіном і не може бути змінена.',
    loadingProfile: 'Завантаження профілю…',
    sectionPersonalInfo: 'Особиста інформація',
    fieldEmailLogin: 'Ел. пошта (логін)',
    fieldFirstName: 'Ім\'я',
    fieldLastName: 'Прізвище',
    fieldTelephone: 'Телефон',
    fieldIco: 'Код компанії (IČO)',
    saveProfileBtn: 'Зберегти профіль',
    savingProfileBtn: 'Збереження…',
    sectionChangePassword: 'Зміна пароля',
    fieldCurrentPassword: 'Поточний пароль',
    fieldNewPassword: 'Новий пароль',
    fieldConfirmPassword: 'Підтвердити новий пароль',
    changePasswordBtn: 'Змінити пароль',
    changingPasswordBtn: 'Оновлення…',
    profileSaved: 'Особисту інформацію збережено',
    passwordChanged: 'Пароль успішно змінено',
    errorPasswordTooShort: 'Новий пароль повинен містити щонайменше 8 символів',
    errorPasswordMismatch: 'Новий пароль і підтвердження не збігаються',
    ttlockDescription: 'Підключіть обліковий запис TTLock для автоматичної генерації PIN-кодів до бронювань.',
    ttlockNoAccount: 'Обліковий запис TTLock не підключено.',
    ttlockConnectedAs: 'Підключено як',
    ttlockTokenExpired: 'Токен прострочено — підключіться знову нижче',
    ttlockTokenValid: (date, days) => `Токен дійсний · закінчується ${date} (залишилось ${days} дн.)`,
    ttlockReconnectTitle: 'Перепідключити обліковий запис',
    ttlockConnectTitle: 'Підключити обліковий запис',
    ttlockUsernameLabel: 'Ім\'я користувача TTLock',
    ttlockPasswordLabel: 'Пароль TTLock',
    connectingBtn: 'Підключення…',
    connectBtn: 'Підключити',
    locksTitle: (count) => `Замки (${count})`,
    noLocksFound: 'Замків не знайдено.',
    lockActive: 'Активний',
    lockInactive: 'Неактивний',
    configureBtn: 'Налаштувати',
    ttlockConnected: 'Обліковий запис TTLock успішно підключено.',
    cleanersDescription: 'Керуйте персоналом прибирання та їхніми приватними посиланнями на розклад.',
    addCleanerBtn: '+ Додати прибиральника',
    editCleanerTitle: 'Редагувати прибиральника',
    newCleanerTitle: 'Новий прибиральник',
    cleanerNameLabel: 'Ім\'я *',
    cleanerScheduleDaysLabel: 'Видимість розкладу (дні)',
    cleanerScheduleDaysHint: 'Скільки днів наперед прибиральник може бачити.',
    cleanerApartmentsLabel: 'Призначені апартаменти',
    noApartmentsFound: 'Апартаментів не знайдено.',
    updateBtn: 'Оновити',
    createBtn: 'Створити',
    loadingCleaners: 'Завантаження…',
    noCleaners: 'Прибиральників ще немає. Натисніть «+ Додати прибиральника» для початку.',
    daysVisible: 'днів видно',
    noApartmentsAssigned: 'Апартаменти не призначені',
    copyLinkBtn: 'Копіювати посилання',
    copiedBtn: '✓ Скопійовано',
    cleanerNameRequired: 'Ім\'я є обов\'язковим',
    cleanerUpdated: 'Прибиральника оновлено.',
    cleanerCreated: 'Прибиральника створено.',
    cleanerDeleted: 'Прибиральника видалено.',
    confirmDeleteCleaner: 'Видалити цього прибиральника?',
    contactsDescription: 'Контакти для повторного використання, які можна призначати апартаментам замість щоразового введення.',
    addContactBtn: '+ Додати контакт',
    editContactTitle: 'Редагувати контакт',
    newContactTitle: 'Новий контакт',
    contactNameLabel: 'Ім\'я *',
    contactPhoneLabel: 'Телефон',
    contactEmailLabel: 'Ел. пошта',
    contactCompanyLabel: 'Назва компанії/юридичної особи',
    contactIcoLabel: 'IČO/DIČ (ідент. номер/податковий номер)',
    contactAddressLabel: 'Місце проживання або юридична адреса',
    savingBtn: 'Збереження…',
    saveBtn: 'Зберегти',
    loadingContacts: 'Завантаження контактів…',
    noContacts: 'Контактів ще немає.',
    contactsEmptyBody: 'Контакти — це люди для повторного використання (власник, управитель, прибиральник), яких можна призначити апартаменту. Гість бачитиме ім\'я та телефон контакту у формі реєстрації.',
    noContactDetails: 'Немає контактних даних',
    contactUpdated: 'Контакт оновлено.',
    contactCreated: 'Контакт створено.',
    confirmDeleteContact: (name) => `Видалити контакт «${name}»?`,
  },
};
